import { environment } from 'src/environments/environment';
import {
  AnalyticsProvider,
  EventProperties,
  UserIdentity,
  UserProperties,
} from './analytics.types';

export class Ga4Provider implements AnalyticsProvider {
  readonly name = 'ga4';
  private ready = false;
  private static readonly SCRIPT_SRC = 'https://www.googletagmanager.com/gtag/js';

  isReady(): boolean {
    return this.ready;
  }

  init(): Promise<void> {
    return new Promise<void>((resolve) => {
      const measurementId = environment.analytics?.ga4MeasurementId;
      if (!measurementId) {
        resolve(); // no-op provider — silent
        return;
      }
      // If gtag is already loaded (HMR or a manual snippet), don't re-inject.
      if (this.gtagLoaded()) {
        this.configure(measurementId);
        this.ready = true;
        resolve();
        return;
      }
      try {
        const w = window as any;
        w.dataLayer = w.dataLayer || [];
        w.gtag = w.gtag || function () {
          w.dataLayer.push(arguments);
        };
        w.gtag('js', new Date());

        const script = document.createElement('script');
        script.async = true;
        script.src = `${Ga4Provider.SCRIPT_SRC}?id=${encodeURIComponent(measurementId)}`;
        script.onload = () => {
          this.configure(measurementId);
          this.ready = true;
          resolve();
        };
        script.onerror = () => {
          // Ad blocker, offline, etc. Resolve so bootstrap continues; stay off.
          console.warn('[analytics/ga4] script failed to load');
          resolve();
        };
        document.head.appendChild(script);
      } catch (err) {
        console.warn('[analytics/ga4] init failed', err);
        resolve();
      }
    });
  }

  identify(identity: UserIdentity): void {
    if (!this.ready) return;
    try {
      const measurementId = environment.analytics?.ga4MeasurementId;
      if (!measurementId) return;
      // Re-configure with a user_id — GA4 uses this for cross-device stitching
      // and lets the "user_id reporting identity" surface stitched sessions.
      (window as any).gtag('config', measurementId, {
        user_id: identity.distinctId,
        // Prevents re-firing an automatic page_view during config re-emission;
        // we handle SPA route views manually via trackRoute().
        send_page_view: false,
      });
      if (identity.properties) {
        (window as any).gtag('set', 'user_properties', this.mapUserProps(identity));
      }
    } catch (err) {
      console.warn('[analytics/ga4] identify failed', err);
    }
  }

  setUserProperties(props: UserProperties): void {
    if (!this.ready) return;
    try {
      (window as any).gtag('set', 'user_properties', this.stripPii(props));
    } catch (err) {
      console.warn('[analytics/ga4] setUserProperties failed', err);
    }
  }

  track(event: string, properties?: EventProperties): void {
    if (!this.ready) return;
    try {
      (window as any).gtag('event', event, this.sanitizeEventProps(properties));
    } catch (err) {
      console.warn('[analytics/ga4] track failed', event, err);
    }
  }

  trackRoute(path: string, title?: string): void {
    if (!this.ready) return;
    try {
      const measurementId = environment.analytics?.ga4MeasurementId;
      if (!measurementId) return;
      // GA4 recommends a `page_view` event on SPA routes with page_path +
      // page_title so it can build path-scoped session reports.
      (window as any).gtag('event', 'page_view', {
        page_path: path,
        page_title: title || document.title || '',
        page_location: window.location.href,
      });
    } catch (err) {
      console.warn('[analytics/ga4] trackRoute failed', err);
    }
  }

  reset(): void {
    if (!this.ready) return;
    try {
      const measurementId = environment.analytics?.ga4MeasurementId;
      if (!measurementId) return;
      // Reissue config without a user_id to detach the identity from
      // subsequent events. GA4 has no explicit `reset()`; this is the
      // canonical pattern.
      (window as any).gtag('config', measurementId, {
        user_id: null,
        send_page_view: false,
      });
    } catch (err) {
      console.warn('[analytics/ga4] reset failed', err);
    }
  }

  // ── Helpers ─────────────────────────────────────────────────────────

  private configure(measurementId: string): void {
    (window as any).gtag('config', measurementId, {
      debug_mode: !!environment.analytics?.debug,
      // We fire page_view manually per SPA nav — turn off the automatic one
      // to avoid duplicate views on initial load.
      send_page_view: false,
      anonymize_ip: true,
      allow_google_signals: true,
    });
  }

  private gtagLoaded(): boolean {
    return typeof (window as any).gtag === 'function' && !!(window as any).dataLayer;
  }

  /** GA4 has a hard 25-property-per-event limit and a 40-char key limit.
   *  Also: `role`, `path`, etc. are fine; PII like `email` should never be
   *  sent as event properties (only as user properties, and even then only
   *  after your privacy review). */
  private sanitizeEventProps(props?: EventProperties): Record<string, unknown> {
    if (!props) return {};
    const out: Record<string, unknown> = {};
    let count = 0;
    for (const [key, value] of Object.entries(props)) {
      if (count >= 25) break;
      if (value === undefined || value === null) continue;
      const safeKey = key.substring(0, 40);
      if (typeof value === 'string' && value.length > 100) {
        out[safeKey] = value.substring(0, 100);
      } else if (Array.isArray(value)) {
        // GA4 doesn't accept arrays — join into a CSV so at least the data
        // isn't lost. Callers that need array semantics should split into
        // multiple typed keys (`service_1`, `service_2`, …).
        out[safeKey] = value.join(',').substring(0, 100);
      } else {
        out[safeKey] = value;
      }
      count++;
    }
    return out;
  }

  private mapUserProps(identity: UserIdentity): Record<string, unknown> {
    const props = identity.properties ? { ...identity.properties } : {};
    // GA4 user_properties naming rules: snake_case, ≤24 chars,
    // no reserved names (`user_id` etc.).
    return this.stripPii({
      role: identity.role,
      ...props,
    });
  }

  private stripPii(props: UserProperties | Record<string, unknown>): Record<string, unknown> {
    const { email, phone, name, ...safe } = props as UserProperties;
    // We intentionally drop email/phone/name from GA4 user_properties —
    // GA4's ToS forbids sending PII. If you need to segment by email,
    // hash it upstream and pass `email_hash` instead.
    return safe as Record<string, unknown>;
  }
}
